import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import {
    Box,
    Container,
    VStack,
    HStack,
    Text,
    Button,
    useToast,
    Badge,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Input,
    useDisclosure,
    Heading,
    useColorModeValue,
    Grid,
    Icon,
    SimpleGrid,
    Divider,
    Flex,
    Progress,
    InputGroup,
    InputRightElement,
    IconButton,
    Center,
    Image,
} from '@chakra-ui/react';

import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { FiGift, FiUser, FiTwitter, FiCheck, FiCopy, FiLink } from 'react-icons/fi';
import { FaTwitter, FaDiscord } from 'react-icons/fa';

// Import our new direct wallet connector
// Using standard WalletMultiButton instead of DirectWalletConnector

// Import our new section components
import ProfileSection from '../components/sections/ProfileSection';
import UserStatsSection from '../components/sections/UserStatsSection';
import ContributionSection from '../components/sections/ContributionSection';
import DailyBoxesSection from '../components/sections/DailyBoxesSection';

// Import existing components
import AnimatedBackground from '../components/AnimatedBackground';
import TypewriterMessage from '../components/TypewriterMessage';
import DailyBoxes from '../components/DailyBoxes';
import SocialPrompt from '../components/SocialPrompt';
import ProfileHeader from '../components/ProfileHeader';
import AuctionDashboard from '../components/AuctionDashboard';

// Import referral manager
import { generateReferralLink, extractReferrerFromUrl, storeReferrer } from '../utils/referralManager';
import { getReferrerStats, checkIfReferred, claimAffiliateRewards } from '../utils/affiliateContract';

// Import hooks
import { useUserStats } from '../hooks/useUserStats';
import { useContribution } from '../hooks/useContribution';
import { useAuctionState } from '../hooks/useAuctionState';

// Import context
import { AuctionContext } from '../context/AuctionContext';
import { UserContext } from '../context/UserContext';

// Import utility functions
import { formatWalletAddress } from '../utils/formatters';

// Import utilities
import { AUCTION_DAYS, TASKS } from '../utils/constants';
import { getDailyAllocation } from '../utils/dailyAllocation';

const Auction = () => {
    const wallet = useWallet();
    const { connection } = useConnection();
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isNicknameOpen, onOpen: onNicknameOpen, onClose: onNicknameClose } = useDisclosure();
    
    // State for user profile
    const [userProfile, setUserProfile] = useState(null);
    const [nickname, setNickname] = useState('');
    const [showNicknameModal, setShowNicknameModal] = useState(false);
    const [isUpdatingNickname, setIsUpdatingNickname] = useState(false);
    const [isConnectingTwitter, setIsConnectingTwitter] = useState(false);
    const [isConnectingDiscord, setIsConnectingDiscord] = useState(false);
    
    // Referral state
    const [referralStats, setReferralStats] = useState({
        count: 0,
        volume: 0,
        earnings: 0,
        claimed: 0,
        unclaimed: 0
    });
    const [isLoadingReferrals, setIsLoadingReferrals] = useState(false);
    const [isClaimingRewards, setIsClaimingRewards] = useState(false);
    const [referrer, setReferrer] = useState(null);
    
    // Check for referral on page load
    useEffect(() => {
        const checkReferral = async () => {
            const referrerWallet = extractReferrerFromUrl();
            if (referrerWallet && wallet.publicKey && referrerWallet !== wallet.publicKey.toString()) {
                storeReferrer(referrerWallet);
                toast({
                    title: 'Referral link detected',
                    description: 'You were referred by another user. They will earn 10% of your contributions.',
                    status: 'info',
                    duration: 5000,
                    isClosable: true,
                });
            }
            
            // Check if user was already referred
            if (wallet.connected && wallet.publicKey) {
                try {
                    const referrerPubkey = await checkIfReferred(connection, wallet.publicKey);
                    setReferrer(referrerPubkey);
                } catch (error) {
                    console.error('Error checking referral:', error);
                }
            }
        };
        
        checkReferral();
    }, [wallet.publicKey, wallet.connected, connection, toast]);
    
    // Load referral stats when wallet is connected
    useEffect(() => {
        const loadReferralStats = async () => {
            if (!wallet.connected || !wallet.publicKey) return;
            
            setIsLoadingReferrals(true);
            try {
                // Fetch real data from the blockchain
                const stats = await getReferrerStats(connection, wallet.publicKey);
                setReferralStats(stats);
            } catch (error) {
                console.error('Error loading referral stats:', error);
                toast({
                    title: 'Error loading referrals',
                    description: 'Could not load your referral statistics',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                
                // Fallback to mock data if blockchain data fails
                setReferralStats({
                    count: 0,
                    volume: 0,
                    earnings: 0,
                    claimed: 0,
                    unclaimed: 0
                });
            } finally {
                setIsLoadingReferrals(false);
            }
        };
        
        loadReferralStats();
    }, [wallet.connected, wallet.publicKey, connection, toast]);
    
    // Handle claiming rewards
    const handleClaimRewards = async () => {
        if (!wallet.connected || !wallet.publicKey) {
            toast({
                title: 'Wallet not connected',
                description: 'Please connect your wallet to claim rewards',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        
        if (referralStats.unclaimed <= 0) {
            toast({
                title: 'No rewards to claim',
                description: 'You do not have any unclaimed rewards',
                status: 'info',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        
        setIsClaimingRewards(true);
        try {
            // This would be replaced with actual token account in production
            const userTokenAccount = wallet.publicKey; // Placeholder
            
            // Call the contract to claim rewards
            await claimAffiliateRewards(
                connection,
                wallet,
                referralStats.unclaimed,
                userTokenAccount
            );
            
            toast({
                title: 'Rewards claimed',
                description: `Successfully claimed ${referralStats.unclaimed.toFixed(2)} JINU`,
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
            
            // Refresh stats after claiming
            const updatedStats = await getReferrerStats(connection, wallet.publicKey);
            setReferralStats(updatedStats);
        } catch (error) {
            console.error('Error claiming rewards:', error);
            toast({
                title: 'Error claiming rewards',
                description: error.message || 'An error occurred while claiming rewards',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsClaimingRewards(false);
        }
    };

    // Get user context
    const { 
        userProfile: userContextProfile, 
        isConnectingTwitter: isConnectingTwitterContext, 
        isConnectingDiscord: isConnectingDiscordContext,
        handleTwitterConnect,
        handleDiscordConnect,
        isTaskCompleted
    } = useContext(UserContext);
    
    // Get auction context data
    const {
        handleContribution,
        isContributing,
        currentDay
    } = useContext(AuctionContext);
    
    // Nickname modal state
    const [nicknameModal, setNicknameModal] = useState('');
    const [showNicknameModalState, setShowNicknameModalState] = useState(false);
    const [isUpdatingNicknameState, setIsUpdatingNicknameState] = useState(false);
    
    // Box style for glowing effect
    const glowBoxStyle = {
        p: 6,
        bg: "rgba(16, 16, 16, 0.9)",
        borderRadius: "xl",
        border: "1px solid rgba(153, 69, 255, 0.3)",
        position: "relative",
        overflow: "hidden",
        boxShadow: "0 0 40px rgba(153, 69, 255, 0.3)",
        _before: {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bg: "radial-gradient(circle at center, rgba(153, 69, 255, 0.15), transparent 70%)",
            pointerEvents: "none",
            zIndex: 0
        }
    };
    
    // Handle nickname update
    const handleUpdateNickname = async () => {
        setIsUpdatingNicknameState(true);
        
        try {
            // Implement nickname update logic here
            
            setShowNicknameModalState(false);
            toast({
                title: 'Nickname updated',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error updating nickname:', error);
            toast({
                title: 'Error updating nickname',
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsUpdatingNicknameState(false);
        }
    };
    
    return (
        <Box 
            position="relative" 
            minH="100vh"
            bg="#0D1019"
            pt="80px"
        >
            <AnimatedBackground />

            <Container maxW={{ base: "95vw", md: "85vw" }} py={8}>
                {!wallet.connected ? (
                    <VStack 
                        spacing={6} 
                        align="center"
                        p={8}
                        bg="whiteAlpha.50"
                        borderRadius="xl"
                        backdropFilter="blur(10px)"
                        border="1px solid"
                        borderColor="whiteAlpha.100"
                    >
                        <Icon as={FiGift} boxSize={8} color="green.400" />
                        <VStack spacing={3}>
                            <Heading size="lg" color="white">Welcome to JUNGLE INU</Heading>
                            <Text 
                                color="gray.300" 
                                fontSize="lg" 
                                whiteSpace="nowrap" 
                                overflow="visible"
                                textAlign="center"
                                mb={2}
                            >
                                Connect your wallet to participate in the 50 day Fair Launch of $JINU !
                            </Text>
                            <TypewriterMessage />
                            <Box
                                mt={4}
                                sx={{
                                    '& .wallet-adapter-button': {
                                        backgroundColor: 'transparent',
                                        color: '#8247E5',
                                        border: '2px solid #8247E5',
                                        borderRadius: '9999px',
                                        padding: '12px 32px',
                                        fontSize: '16px',
                                        fontWeight: 'bold',
                                        transition: 'all 0.2s',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: '#8247E5',
                                            color: 'white',
                                        },
                                        '&:not([disabled])': {
                                            backgroundColor: 'transparent',
                                            '&:hover': {
                                                backgroundColor: '#8247E5',
                                            },
                                        },
                                        '.wallet-adapter-button-start-icon': {
                                            marginRight: '8px',
                                        },
                                        '.wallet-adapter-button-end-icon': {
                                            marginLeft: '8px',
                                        },
                                        '.wallet-adapter-collapse': {
                                            width: '100%',
                                            height: '100%',
                                        },
                                        '.wallet-adapter-dropdown': {
                                            display: 'inline-block',
                                            padding: '0 8px',
                                        },
                                        '.wallet-adapter-dropdown-list': {
                                            background: '#2c2d30',
                                            borderRadius: '10px',
                                            padding: '8px',
                                            boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.6)',
                                            marginTop: '8px',
                                        },
                                        '.wallet-adapter-dropdown-list-item': {
                                            padding: '8px 16px',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'rgba(130, 71, 229, 0.2)',
                                            },
                                        },
                                    },
                                }}
                            >
                                <WalletMultiButton />
                            </Box>
                        </VStack>
                        <HStack spacing={4} flexWrap="wrap" justify="center">
                            <Badge colorScheme="purple" p={2} borderRadius="full">50 Day Auction</Badge>
                            <Badge colorScheme="green" p={2} borderRadius="full">1.5x Early Bonus</Badge>
                            <Badge colorScheme="blue" p={2} borderRadius="full">10% Referral Rewards</Badge>
                        </HStack>
                    </VStack>
                ) : (
                    <VStack spacing={6} align="stretch">
                        {/* Profile Section */}
                        <ProfileSection walletButton={<WalletMultiButton />} />
                        
                        {/* User Stats and Contribution Grid */}
                        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6} mb={8}>
                            <UserStatsSection />
                            <ContributionSection />
                        </Grid>

                        {/* Social Connect Section */}
                        <Box pt={4}>
                            <Text color="white" fontWeight="medium" mb={4}>Connect & Earn</Text>
                            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                                <VStack 
                                    align="stretch" 
                                    spacing={4}
                                    {...glowBoxStyle}
                                >
                                    <HStack justify="space-between" align="center">
                                        <HStack spacing={3}>
                                            <Icon as={FiTwitter} color="#1D9BF0" boxSize={{ base: 5, sm: 6 }} />
                                            <Text color="white" fontSize="md" fontWeight="medium">Twitter</Text>
                                        </HStack>
                                        <Button
                                            bg={userContextProfile?.socials?.twitter?.connected ? "rgba(29, 155, 240, 0.2)" : "transparent"}
                                            border="2px solid"
                                            borderColor="#1D9BF0"
                                            color="#1D9BF0"
                                            _hover={{
                                                bg: '#1D9BF0',
                                                color: 'white',
                                            }}
                                            rounded="full"
                                            px={6}
                                            size="sm"
                                            onClick={handleTwitterConnect}
                                            isLoading={isConnectingTwitterContext}
                                            leftIcon={userContextProfile?.socials?.twitter?.connected ? <FiCheck /> : <FaTwitter />}
                                        >
                                            {userContextProfile?.socials?.twitter?.connected ? 'Connected' : 'Connect'}
                                        </Button>
                                    </HStack>

                                    {userContextProfile?.socials?.twitter?.connected && (
                                        <Text color="gray.400" fontSize="sm" mt={-2}>
                                            Connected as @{userContextProfile.socials.twitter.handle}
                                        </Text>
                                    )}

                                    <Text color="gray.400" fontSize="sm" mt={-1}>Up to +25% Bonus</Text>

                                    {userContextProfile?.socials?.twitter?.connected && (
                                        <VStack align="stretch" spacing={2}>
                                            <HStack justify="space-between">
                                                <Text color="gray.300" fontSize={{ base: "xs", sm: "sm" }}>Follow @PalmHeist</Text>
                                                <Badge 
                                                    bg={isTaskCompleted('twitter', TASKS.TWITTER_FOLLOW) ? "rgba(29, 155, 240, 0.4)" : "rgba(29, 155, 240, 0.2)"} 
                                                    color="#1D9BF0"
                                                    px={2}
                                                    py={1}
                                                    borderRadius="full"
                                                    fontSize="xs"
                                                >
                                                    {isTaskCompleted('twitter', TASKS.TWITTER_FOLLOW) ? '+0.25%' : '+0.25%'}
                                                </Badge>
                                            </HStack>
                                            <HStack justify="space-between">
                                                <Text color="gray.300" fontSize={{ base: "xs", sm: "sm" }}>Add $JINU to bio</Text>
                                                <Badge 
                                                    bg={isTaskCompleted('twitter', TASKS.TWITTER_BIO) ? "rgba(29, 155, 240, 0.4)" : "rgba(29, 155, 240, 0.2)"} 
                                                    color="#1D9BF0"
                                                    px={2}
                                                    py={1}
                                                    borderRadius="full"
                                                    fontSize="xs"
                                                >
                                                    {isTaskCompleted('twitter', TASKS.TWITTER_BIO) ? '+0.25%' : '+0.25%'}
                                                </Badge>
                                            </HStack>
                                            <HStack justify="space-between">
                                                <Text color="gray.300" fontSize={{ base: "xs", sm: "sm" }}>Retweet announcement</Text>
                                                <Badge 
                                                    bg={isTaskCompleted('twitter', TASKS.TWITTER_RETWEET) ? "rgba(29, 155, 240, 0.4)" : "rgba(29, 155, 240, 0.2)"} 
                                                    color="#1D9BF0"
                                                    px={2}
                                                    py={1}
                                                    borderRadius="full"
                                                    fontSize="xs"
                                                >
                                                    {isTaskCompleted('twitter', TASKS.TWITTER_RETWEET) ? '+0.25%' : '+0.25%'}
                                                </Badge>
                                            </HStack>
                                        </VStack>
                                    )}
                                </VStack>

                                <VStack 
                                    align="stretch" 
                                    spacing={4}
                                    {...glowBoxStyle}
                                >
                                    <HStack justify="space-between" align="center">
                                        <HStack spacing={3}>
                                            <Icon as={FaDiscord} color="#5865F2" boxSize={{ base: 5, sm: 6 }} />
                                            <Text color="white" fontSize="md" fontWeight="medium">Discord</Text>
                                        </HStack>
                                        <Button
                                            bg={userContextProfile?.socials?.discord?.connected ? "rgba(88, 101, 242, 0.2)" : "transparent"}
                                            border="2px solid"
                                            borderColor="#5865F2"
                                            color="#5865F2"
                                            _hover={{
                                                bg: '#5865F2',
                                                color: 'white',
                                            }}
                                            rounded="full"
                                            px={6}
                                            size="sm"
                                            onClick={handleDiscordConnect}
                                            isLoading={isConnectingDiscordContext}
                                            leftIcon={userContextProfile?.socials?.discord?.connected ? <FiCheck /> : <FaDiscord />}
                                        >
                                            {userContextProfile?.socials?.discord?.connected ? 'Connected' : 'Connect'}
                                        </Button>
                                    </HStack>

                                    {userContextProfile?.socials?.discord?.connected && (
                                        <Text color="gray.400" fontSize="sm" mt={-2}>
                                            Connected as {userContextProfile.socials.discord.username}
                                        </Text>
                                    )}

                                    <Text color="gray.400" fontSize="sm" mt={-1}>Up to +25% Bonus</Text>

                                    {userContextProfile?.socials?.discord?.connected && (
                                        <VStack align="stretch" spacing={2}>
                                            <HStack justify="space-between">
                                                <Text color="gray.300" fontSize={{ base: "xs", sm: "sm" }}>Join Discord server</Text>
                                                <Badge 
                                                    bg={isTaskCompleted('discord', TASKS.DISCORD_JOIN) ? "rgba(88, 101, 242, 0.4)" : "rgba(88, 101, 242, 0.2)"} 
                                                    color="#5865F2"
                                                    px={2}
                                                    py={1}
                                                    borderRadius="full"
                                                    fontSize="xs"
                                                >
                                                    {isTaskCompleted('discord', TASKS.DISCORD_JOIN) ? '+0.25%' : '+0.25%'}
                                                </Badge>
                                            </HStack>
                                            <HStack justify="space-between">
                                                <Text color="gray.300" fontSize={{ base: "xs", sm: "sm" }}>Verify in Discord</Text>
                                                <Badge 
                                                    bg={isTaskCompleted('discord', TASKS.DISCORD_VERIFY) ? "rgba(88, 101, 242, 0.4)" : "rgba(88, 101, 242, 0.2)"} 
                                                    color="#5865F2"
                                                    px={2}
                                                    py={1}
                                                    borderRadius="full"
                                                    fontSize="xs"
                                                >
                                                    {isTaskCompleted('discord', TASKS.DISCORD_VERIFY) ? '+0.25%' : '+0.25%'}
                                                </Badge>
                                            </HStack>
                                            <HStack justify="space-between">
                                                <Text color="gray.300" fontSize={{ base: "xs", sm: "sm" }}>React to announcement</Text>
                                                <Badge 
                                                    bg={isTaskCompleted('discord', TASKS.DISCORD_REACT) ? "rgba(88, 101, 242, 0.4)" : "rgba(88, 101, 242, 0.2)"} 
                                                    color="#5865F2"
                                                    px={2}
                                                    py={1}
                                                    borderRadius="full"
                                                    fontSize="xs"
                                                >
                                                    {isTaskCompleted('discord', TASKS.DISCORD_REACT) ? '+0.25%' : '+0.25%'}
                                                </Badge>
                                            </HStack>
                                        </VStack>
                                    )}
                                </VStack>

                                <VStack 
                                    align="stretch" 
                                    spacing={4}
                                    {...glowBoxStyle}
                                >
                                    <HStack justify="space-between" align="center">
                                        <HStack spacing={3}>
                                            <Icon as={FiLink} color="#9945FF" boxSize={{ base: 5, sm: 6 }} />
                                            <Text color="white" fontSize="md" fontWeight="medium">Referral</Text>
                                        </HStack>
                                        <Button
                                            bg="transparent"
                                            border="2px solid"
                                            borderColor="#9945FF"
                                            color="#9945FF"
                                            _hover={{
                                                bg: '#9945FF',
                                                color: 'white',
                                            }}
                                            rounded="full"
                                            px={6}
                                            size="sm"
                                            onClick={() => {
                                                if (!wallet.connected || !wallet.publicKey) {
                                                    toast({
                                                        title: 'Wallet not connected',
                                                        description: 'Please connect your wallet to generate a referral link',
                                                        status: 'warning',
                                                        duration: 3000,
                                                        isClosable: true,
                                                    });
                                                    return;
                                                }
                                                
                                                const referralLink = generateReferralLink(wallet.publicKey.toString());
                                                navigator.clipboard.writeText(referralLink);
                                                toast({
                                                    title: 'Referral link copied',
                                                    description: 'Share this link with friends to earn 10% of their contributions',
                                                    status: 'success',
                                                    duration: 3000,
                                                    isClosable: true,
                                                });
                                            }}
                                            leftIcon={<FiCopy />}
                                            isDisabled={!wallet.connected}
                                        >
                                            Copy Link
                                        </Button>
                                    </HStack>

                                    <Text color="gray.400" fontSize="sm">Earn 10% of referred contributions</Text>

                                    <VStack align="stretch" spacing={2}>
                                        <HStack justify="space-between">
                                            <Text color="gray.300" fontSize={{ base: "xs", sm: "sm" }}>Total Referrals</Text>
                                            <Text color="white" fontSize={{ base: "xs", sm: "sm" }} fontWeight="bold">
                                                {referralStats.count}
                                            </Text>
                                        </HStack>
                                        <HStack justify="space-between">
                                            <Text color="gray.300" fontSize={{ base: "xs", sm: "sm" }}>Referral SOL</Text>
                                            <Text color="white" fontSize={{ base: "xs", sm: "sm" }} fontWeight="bold">
                                                {referralStats.volume.toFixed(2)} SOL
                                            </Text>
                                        </HStack>
                                        <HStack justify="space-between">
                                            <Text color="gray.300" fontSize={{ base: "xs", sm: "sm" }}>Referral Earnings</Text>
                                            <Text color="white" fontSize={{ base: "xs", sm: "sm" }} fontWeight="bold">
                                                {referralStats.earnings.toFixed(2)} SOL
                                            </Text>
                                        </HStack>
                                        
                                        {/* Claim button - only visible on Day 51 or later */}
                                        {currentDay >= 51 && (
                                            <Button
                                                mt={2}
                                                colorScheme="purple"
                                                size="sm"
                                                isDisabled={referralStats.unclaimed <= 0}
                                                onClick={handleClaimRewards}
                                            >
                                                Claim Earnings
                                            </Button>
                                        )}
                                    </VStack>
                                </VStack>
                            </SimpleGrid>
                        </Box>

                        {/* Auction Dashboard */}
                        <AuctionDashboard />

                        {/* Daily Boxes Section */}
                        <DailyBoxesSection />
                    </VStack>
                )}
            </Container>

            {/* Nickname Modal */}
            <Modal isOpen={showNicknameModalState} onClose={() => setShowNicknameModalState(false)}>
                <ModalOverlay backdropFilter="blur(10px)" />
                <ModalContent bg="rgba(16, 16, 16, 0.95)" border="1px solid rgba(153, 69, 255, 0.3)" borderRadius="xl">
                    <ModalHeader color="white">Set Display Name</ModalHeader>
                    <ModalCloseButton color="white" />
                    <ModalBody>
                        <Input
                            placeholder="Enter your display name"
                            value={nicknameModal}
                            onChange={(e) => setNicknameModal(e.target.value)}
                            bg="rgba(0,0,0,0.2)"
                            border="1px solid rgba(153, 69, 255, 0.3)"
                            color="white"
                            _placeholder={{ color: "gray.500" }}
                            _focus={{ borderColor: "purple.500" }}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            bg="transparent"
                            border="1px solid"
                            borderColor="gray.500"
                            color="gray.300"
                            mr={3}
                            onClick={() => setShowNicknameModalState(false)}
                            _hover={{ bg: "whiteAlpha.100" }}
                        >
                            Cancel
                        </Button>
                        <Button
                            bg="purple.600"
                            color="white"
                            _hover={{ bg: "purple.700" }}
                            onClick={handleUpdateNickname}
                            isLoading={isUpdatingNicknameState}
                        >
                            Save
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default Auction;
